import React from "react";
import { Controller, useForm } from "react-hook-form";
import { Checkbox, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";

import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import { useAuth } from "hooks";
import { useLazyDownloadDeliveriesByDriverQuery } from "store/services/deliveryApi";
import { useLazyGetDDLDriverListQuery } from "store/services/driverApi";
import { exportExcelFile } from "utils/exportExcelFile";
import moment from "moment";
import { toast } from "react-toastify";

const INITIAL_STATE = { dateRange: [], driverId: null };

const DeliveryExcel = ({ isBusy = false }) => {
  const [showDialog, setShowDialog] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const { viewEntity } = useAuth();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: INITIAL_STATE,
  });

  const [fetchDeliveries] = useLazyDownloadDeliveriesByDriverQuery();
  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] =
    useLazyGetDDLDriverListQuery();

  const handleDownload = async (data) => {
    const { dateRange } = data;

    const fromDate = moment(dateRange[0]).format("MM-DD-YYYY");
    const toDate = moment(dateRange[1]).format("MM-DD-YYYY");

    const payload = {
      driverId: data?.driverId?._id,
      fromDate,
      toDate,
    };

    try {
      const response = await fetchDeliveries(payload).unwrap();
      if (!response.data || response.data.length === 0) {
        toast.error("No Data Found!");
        return;
      }

      const formattedData = response.data.map((row, index) => ({
        SN: index + 1,
        "Completion ID": row.fulfillmentCompletionId || "N/A",
        "Driver ID": row.sendOut?.driver?.driverId || "N/A",
        "Vehicle ID": row.sendOut?.vehicle?.vehicle_id || "N/A",
        "Shipment ID":
          row.shipmentrequest?.map((s) => s.shipmentId).join(", ") || "N/A",
        "Delivery Notes": row?.deliveryNotes || "N/A",
        "Responsible User": row?.responsibleUser?.name || "N/A",
        "Delivery Time": row?.actualDeliveryTime || "N/A",
      }));

      exportExcelFile({
        sheetData: formattedData,
        fileName: `Delivery_${data?.driverId?.driverId || "All"}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`,
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed!");
    } finally {
      handleReset();
    }
  };

  const handleReset = () => {
    setShowDialog(false);
    reset(INITIAL_STATE);
  };

  const handleOpenDialog = () => {
    setShowDialog(true);
    fetchDrivers({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
  };

  return (
    <>
      <Tooltip title="Download Excel" arrow>
        <IconButton color="success" onClick={handleOpenDialog}>
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Delivery"
        dialogIcon={() => <FileDownloadOutlined />}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={showForm}
              onChange={(e) => setShowForm(e.target.checked)}
              icon={<CheckBoxOutlineBlankOutlined />}
              checkedIcon={<CheckBoxOutlined />}
            />
          }
          label="Filter by specific Driver"
        />

        {showForm && (
          <AppForm loading={isBusy}>
            <AppForm.Content>
              <Controller
                name="driverId"
                control={control}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Driver"
                    options={drivers || []}
                    getOptionLabel={(option) => option.driverId}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value?._id
                    }
                    loading={loadingDrivers}
                    loadingText="Loading drivers..."
                    onChange={(_, value) => field.onChange(value)}
                    value={field.value}
                  />
                )}
              />
            </AppForm.Content>
          </AppForm>
        )}

        <Controller
          name="dateRange"
          control={control}
          rules={{ required: "Date range is required" }}
          render={({ field, fieldState: { error } }) => (
            <DateRangeToggle
              {...field}
              label="Select Date Range"
              error={!!error}
              helperText={error?.message}
              required
            />
          )}
        />
      </AppConfirmDialog>
    </>
  );
};

export default DeliveryExcel;
